import { createTheme } from '@mui/material/styles';
import { THEMES, DEFAULT_THEME_KEY } from './config/themes';
import { getActiveThemeKey } from './config/default';

// Build a MUI theme for a given theme key. Palette/mode are derived from the
// theme's color set so MUI components (inputs, menus, tooltips…) adapt too.
export const buildTheme = (key) => {
  const def = THEMES[key] || THEMES[DEFAULT_THEME_KEY];
  const c = def.colors;
  const isLight = def.mode === 'light';

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1386,
        xl: 2200
      }
    },
    palette: {
      mode: def.mode,
      primary: {
        main: c.DARKBLUE,
        light: c.BG_BLACK
      },
      secondary: {
        main: c.DEEPBLUE,
        light: c.TOURQUISE
      },
      Supportive: {
        main: c.LIGHTGREY,
        light: c.MUSTARD
      },
      background: {
        default: c.BG_BLACK,
        paper: c.NAV_BLACK
      },
      text: {
        primary: c.TEXTGREY,
        secondary: c.LIGHTGREY
      }
    },
    text: {
      fontFamily: 'Roboto',
      color: c.WHITE
    },
    typography: {
      // Light surfaces make thin text look faint — nudge the default weight up.
      fontWeightRegular: isLight ? 500 : 400,
      h1: {
        fontFamily: 'Roboto',
        fontSize: '1.7rem',
        fontWeight: 300,
        color: c.WHITE
      }
    },
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight ? '#1F2430' : 'white',
            border: `1px solid ${isLight ? '#1F2430' : 'black'}`,
            color: isLight ? '#ffffff' : 'black'
          }
        }
      },
      // Standard input underline. In light (cream surfaces) use a dark hairline
      // so fields don't blend in. In dark, color it to match the left section
      // background (Canvas_Left_BG) so it reads as a seamless dark line instead
      // of MUI's bright default white underline under canvas fields / line items.
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:before': {
              borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.30)' : c.Canvas_Left_BG}`
            },
            '&:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.55)' : c.Canvas_Left_BG}`
            }
          }
        }
      },
      ...(isLight
        ? {
            MuiOutlinedInput: {
              styleOverrides: {
                notchedOutline: { borderColor: 'rgba(0,0,0,0.25)' }
              }
            }
          }
        : {})
    }
  });
};

// Singleton kept for the many `import theme from '../theme'` consumers.
// `refreshMuiTheme` mutates this same object in place so those imports stay
// valid after a theme switch.
const theme = buildTheme(getActiveThemeKey());

export const refreshMuiTheme = (key) => {
  Object.assign(theme, buildTheme(key));
  return theme;
};

export default theme;
